import { useState } from 'react';
import { useInstallPrompt } from '../hooks/useInstallPrompt.js';

interface InstallBannerProps {
  /** Compact mode for inline placement (e.g., inside done step) */
  compact?: boolean;
}

export default function InstallBanner({ compact }: InstallBannerProps) {
  const { canInstall, isIOS, promptInstall, dismiss, hasNativePrompt } = useInstallPrompt();
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  if (!canInstall) return null;

  const handleInstall = async () => {
    if (hasNativePrompt) {
      await promptInstall();
    } else if (isIOS) {
      setShowIOSGuide(true);
    }
  };

  // iOS step-by-step guide overlay
  if (showIOSGuide) {
    return (
      <div className={`${compact ? '' : 'mb-8'} card p-5 border border-brand-700 bg-brand-900/20`}>
        <div className="flex justify-between items-start mb-3">
          <h3 className="font-bold text-sm">Add to Home Screen</h3>
          <button onClick={() => setShowIOSGuide(false)} className="text-surface-500 text-xs">Close</button>
        </div>
        <ol className="space-y-3 text-sm">
          <li className="flex gap-3 items-start">
            <span className="w-6 h-6 rounded-full bg-brand-600 text-white text-xs font-bold flex items-center justify-center shrink-0">1</span>
            <span className="text-surface-300">
              Tap the <strong className="text-white">Share</strong> button
              <svg className="inline w-4 h-4 ml-1 -mt-0.5 text-brand-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              {' '}at the bottom of Safari
            </span>
          </li>
          <li className="flex gap-3 items-start">
            <span className="w-6 h-6 rounded-full bg-brand-600 text-white text-xs font-bold flex items-center justify-center shrink-0">2</span>
            <span className="text-surface-300">Scroll down and tap <strong className="text-white">"Add to Home Screen"</strong></span>
          </li>
          <li className="flex gap-3 items-start">
            <span className="w-6 h-6 rounded-full bg-brand-600 text-white text-xs font-bold flex items-center justify-center shrink-0">3</span>
            <span className="text-surface-300">Tap <strong className="text-white">"Add"</strong> — done! Open from your home screen anytime.</span>
          </li>
        </ol>
      </div>
    );
  }

  if (compact) {
    return (
      <button
        onClick={handleInstall}
        className="w-full bg-surface-800 border border-surface-700 hover:border-brand-500 text-white font-semibold rounded-xl py-3 transition-colors flex items-center justify-center gap-2"
      >
        <svg className="w-5 h-5 text-brand-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        Add to Home Screen
      </button>
    );
  }

  return (
    <div className="card p-5 mb-8 flex items-center gap-4 border border-brand-700/50 bg-brand-900/10">
      <div className="w-12 h-12 rounded-xl bg-brand-600/20 flex items-center justify-center shrink-0">
        <svg className="w-6 h-6 text-brand-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">Add to Home Screen</p>
        <p className="text-surface-500 text-xs mt-0.5">Instant access — no QR code needed</p>
      </div>
      <div className="flex gap-2 shrink-0">
        <button
          onClick={handleInstall}
          className="bg-brand-600 hover:bg-brand-700 text-white text-xs font-bold px-4 py-2 rounded-lg transition-colors"
        >
          Install
        </button>
        <button onClick={dismiss} className="text-surface-600 text-xs px-2 py-2 hover:text-surface-400 transition-colors">
          Later
        </button>
      </div>
    </div>
  );
}
